
import { useState, KeyboardEvent } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tag, Edit3, Save, X, Plus, Trash2 } from "lucide-react";
import { Candidate } from "../types";
import { useUpdateCandidateProfile } from "@/hooks/mutations/useUpdateCandidateProfile"; // Corrected path
import { toast } from "sonner";

export interface SkillsProps {
  candidate: Candidate;
}

export const Skills = ({ candidate }: SkillsProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedSkills, setEditedSkills] = useState<string[]>(candidate.skills || []);
  const [newSkillInput, setNewSkillInput] = useState("");
  const { updateCandidateProfile, isUpdatingProfile } = useUpdateCandidateProfile();

  const handleEdit = () => {
    setEditedSkills(candidate.skills || []);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setNewSkillInput("");
    setEditedSkills(candidate.skills || []); // Reset to original skills
  };

  const handleAddSkill = () => {
    if (newSkillInput.trim() && !editedSkills.includes(newSkillInput.trim())) {
      setEditedSkills([...editedSkills, newSkillInput.trim()]);
      setNewSkillInput("");
    } else if (editedSkills.includes(newSkillInput.trim())) {
      toast.info(`Skill "${newSkillInput.trim()}" already added.`);
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setEditedSkills(editedSkills.filter((skill) => skill !== skillToRemove));
  };

  const handleSave = () => {
    updateCandidateProfile(
      { candidateId: candidate.id, data: { skills: editedSkills } },
      {
        onSuccess: (updatedCandidate) => {
          setIsEditing(false);
          if (updatedCandidate?.skills !== undefined) {
            setEditedSkills(updatedCandidate.skills);
          }
          // Toast is handled by the hook
        },
        onError: () => {
          // Toast is handled by the hook
        }
      }
    );
  };
  
  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkill();
    }
  };

  if (!candidate && !isEditing) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-xl">
          <Tag className="h-5 w-5 text-primary" />
          Skills
        </CardTitle>
        {!isEditing && (
          <Button variant="outline" size="sm" onClick={handleEdit}>
            {candidate.skills?.length ? <Edit3 className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            {candidate.skills?.length ? "Edit Skills" : "Add Skills"}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Enter a skill (e.g., React)"
                value={newSkillInput}
                onChange={(e) => setNewSkillInput(e.target.value)}
                onKeyDown={handleInputKeyDown}
                className="flex-grow"
                disabled={isUpdatingProfile}
              />
              <Button size="sm" onClick={handleAddSkill} disabled={isUpdatingProfile || !newSkillInput.trim()}>
                <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Add</span>
              </Button>
            </div>
            {editedSkills.length > 0 && (
              <div className="flex flex-wrap gap-2 border p-3 rounded-md min-h-[40px]">
                {editedSkills.map((skill) => (
                  <Badge key={skill} variant="secondary" className="py-1 px-2 group relative">
                    {skill}
                    <button
                      onClick={() => handleRemoveSkill(skill)}
                      className="ml-1.5 p-0.5 rounded-full hover:bg-destructive/20 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Remove ${skill}`}
                      disabled={isUpdatingProfile}
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
             {editedSkills.length === 0 && (
                <p className="text-sm text-muted-foreground italic p-3 border rounded-md min-h-[40px]">No skills added yet. Type a skill above and click "Add".</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={isUpdatingProfile}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isUpdatingProfile}>
                <Save className="h-4 w-4 mr-1" /> {isUpdatingProfile ? "Saving..." : "Save Skills"}
              </Button>
            </div>
          </div>
        ) : (
          candidate.skills?.length ? (
            <div className="flex flex-wrap gap-2">
              {candidate.skills.map((skill) => (
                <Badge key={skill} variant="secondary" className="py-1 px-2">
                  {skill}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground italic">No skills added yet.</p>
          )
        )}
      </CardContent>
    </Card>
  );
};
